// components/AdvancedFilterBuilder.jsx
import React, { useState, useEffect } from "react";
import { Plus, Trash2, Save, Filter } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

const LEAD_FIELDS = [
  // Basic Information
  { value: "fullName", label: "Full Name", type: "string" },
  { value: "firstName", label: "First Name", type: "string" },
  { value: "lastName", label: "Last Name", type: "string" },
  { value: "email", label: "Email", type: "string" },
  { value: "phone", label: "Phone", type: "string" },
  { value: "mobilePhone", label: "Mobile Phone", type: "string" },

  // Company Information
  { value: "companyName", label: "Company Name", type: "string" },
  { value: "companyWebsite", label: "Company Website", type: "string" },
  { value: "companyIndustry", label: "Industry", type: "string" },
  { value: "companySize", label: "Company Size", type: "string" },
  { value: "numberOfEmployees", label: "Number of Employees", type: "number" },
  { value: "annualRevenue", label: "Annual Revenue", type: "decimal" },
  { value: "typeOfBusiness", label: "Type of Business", type: "string" },

  // Job Information
  { value: "jobTitle", label: "Job Title", type: "string" },
  { value: "department", label: "Department", type: "string" },
  { value: "seniority", label: "Seniority", type: "string" },

  // Location
  { value: "country", label: "Country", type: "string" },
  { value: "state", label: "State/Province", type: "string" },
  { value: "city", label: "City", type: "string" },
  { value: "postalCode", label: "Postal Code", type: "string" },

  // Lead Status
  {
    value: "leadStatus",
    label: "Lead Status",
    type: "select",
    options: [
      { value: "lead_pot", label: "Lead Pot" },
      { value: "new", label: "New" },
      { value: "nurture", label: "Nurture" },
      { value: "unqualified", label: "Unqualified" },
      { value: "dead", label: "Dead" },
      { value: "converted", label: "Converted" },
    ],
  },

  // Lead Source
  {
    value: "leadSource",
    label: "Lead Source",
    type: "select",
    options: [
      { value: "Web", label: "Web" },
      { value: "Phone Inquiry", label: "Phone Inquiry" },
      { value: "Partner Referral", label: "Partner Referral" },
      { value: "Purchased List", label: "Purchased List" },
      { value: "Other", label: "Other" },
      { value: "Advertisement", label: "Advertisement" },
      { value: "Employee Referral", label: "Employee Referral" },
      { value: "Trade Show", label: "Trade Show" },
      { value: "Word of mouth", label: "Word of mouth" },
      { value: "Email", label: "Email" },
      { value: "Campaign", label: "Campaign" },
      { value: "Social Media", label: "Social Media" },
    ],
  },
  { value: "referrerName", label: "Referrer Name", type: "string" },
  // Assigned To
  { value: "assignedUser.name", label: "Assigned To", type: "string" },
  // Dates
  { value: "createdAt", label: "Created Date", type: "date" },
  {
    value: "customFields.salesforce.createdDate",
    label: "Salesforce Created Date",
    type: "date",
  },
  { value: "nextFollowUpDate", label: "Next Follow-up Date", type: "date" },
  { value: "lastActivityDate", label: "Last Activity Date", type: "date" },
  { value: "expectedCloseDate", label: "Expected Close Date", type: "date" },
  { value: "contractEndDate", label: "Contract End Date", type: "date" },
  { value: "qualifiedAt", label: "Qualified Date", type: "date" },
  { value: "convertedAt", label: "Converted Date", type: "date" },
  { value: "lastContactedAt", label: "Last Contacted", type: "date" },

  // Tags
  { value: "tags", label: "Tags", type: "array" },
];

const OPERATORS = {
  string: [
    { value: "equals", label: "Equals" },
    { value: "not_equals", label: "Not Equals" },
    { value: "contains", label: "Contains" },
    { value: "not_contains", label: "Does Not Contain" },
    { value: "starts_with", label: "Starts With" },
    { value: "not_starts_with", label: "Does Not Start With" },
    { value: "ends_with", label: "Ends With" },
    { value: "is_empty", label: "Is Empty" },
    { value: "is_not_empty", label: "Is Not Empty" },
    { value: "is_null", label: "Is Null" },
    { value: "is_not_null", label: "Is Not Null" },
  ],
  number: [
    { value: "equals", label: "Equals" },
    { value: "not_equals", label: "Not Equals" },
    { value: "greater_than", label: "Greater Than" },
    { value: "greater_than_or_equal", label: "Greater Than or Equal" },
    { value: "less_than", label: "Less Than" },
    { value: "less_than_or_equal", label: "Less Than or Equal" },
    { value: "between", label: "Between" },
    { value: "is_null", label: "Is Null" },
    { value: "is_not_null", label: "Is Not Null" },
  ],
  decimal: [
    { value: "equals", label: "Equals" },
    { value: "not_equals", label: "Not Equals" },
    { value: "greater_than", label: "Greater Than" },
    { value: "greater_than_or_equal", label: "Greater Than or Equal" },
    { value: "less_than", label: "Less Than" },
    { value: "less_than_or_equal", label: "Less Than or Equal" },
    { value: "between", label: "Between" },
    { value: "is_null", label: "Is Null" },
    { value: "is_not_null", label: "Is Not Null" },
  ],
  date: [
    { value: "equals", label: "Equals" },
    { value: "not_equals", label: "Not Equals" },
    { value: "greater_than", label: "After" },
    { value: "less_than", label: "Before" },
    { value: "between", label: "Between" },
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "this_week", label: "This Week" },
    { value: "last_week", label: "Last Week" },
    { value: "this_month", label: "This Month" },
    { value: "last_month", label: "Last Month" },
    { value: "this_year", label: "This Year" },
    { value: "last_n_days", label: "Last N Days" },
    { value: "next_n_days", label: "Next N Days" },
    { value: "is_null", label: "Is Null" },
    { value: "is_not_null", label: "Is Not Null" },
  ],
  boolean: [
    { value: "is_true", label: "Is True" },
    { value: "is_false", label: "Is False" },
  ],
  select: [
    { value: "equals", label: "Equals" },
    { value: "not_equals", label: "Not Equals" },
    { value: "in", label: "In List" },
    { value: "not_in", label: "Not In List" },
    { value: "is_null", label: "Is Null" },
    { value: "is_not_null", label: "Is Not Null" },
  ],
  array: [
    { value: "contains", label: "Contains" },
    { value: "not_contains", label: "Does Not Contain" },
    { value: "contains_all", label: "Contains All" },
    { value: "is_empty", label: "Is Empty" },
    { value: "is_not_empty", label: "Is Not Empty" },
  ],
};

const OPERATORS_WITHOUT_VALUE = [
  "is_null",
  "is_not_null",
  "is_empty",
  "is_not_empty",
  "is_true",
  "is_false",
  "today",
  "yesterday",
  "this_week",
  "last_week",
  "this_month",
  "last_month",
  "this_year",
];

const AdvancedFilterBuilder = ({
  onApply,
  onSave,
  initialFilters = null,
  showSaveButton = true,
}) => {
  const { isDarkMode } = useTheme();

  const [filterConfig, setFilterConfig] = useState({
    logic: "AND",
    conditions: [],
  });

  useEffect(() => {
    if (initialFilters) {
      setFilterConfig(initialFilters);
    }
  }, [initialFilters]);

  // Shared class helpers
  const selectCls = `px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-blue-500 transition-colors ${
    isDarkMode
      ? "bg-gray-700 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const inputCls = `px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-blue-500 transition-colors ${
    isDarkMode
      ? "bg-gray-700 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const addCondition = (groupIndex = null) => {
    const newCondition = {
      id: Date.now(),
      field: "",
      operator: "",
      value: "",
      dataType: "string",
    };

    if (groupIndex !== null) {
      const newConfig = { ...filterConfig };
      newConfig.conditions[groupIndex].conditions.push(newCondition);
      setFilterConfig(newConfig);
    } else {
      setFilterConfig({
        ...filterConfig,
        conditions: [...filterConfig.conditions, newCondition],
      });
    }
  };

  const addConditionGroup = () => {
    const newGroup = {
      id: Date.now(),
      type: "group",
      logic: "AND",
      conditions: [
        {
          id: Date.now() + 1,
          field: "",
          operator: "",
          value: "",
          dataType: "string",
        },
      ],
    };

    setFilterConfig({
      ...filterConfig,
      conditions: [...filterConfig.conditions, newGroup],
    });
  };

  const removeCondition = (conditionId, groupIndex = null) => {
    if (groupIndex !== null) {
      const newConfig = { ...filterConfig };
      newConfig.conditions[groupIndex].conditions = newConfig.conditions[
        groupIndex
      ].conditions.filter((c) => c.id !== conditionId);

      if (newConfig.conditions[groupIndex].conditions.length === 0) {
        newConfig.conditions = newConfig.conditions.filter(
          (_, i) => i !== groupIndex,
        );
      }

      setFilterConfig(newConfig);
    } else {
      setFilterConfig({
        ...filterConfig,
        conditions: filterConfig.conditions.filter((c) => c.id !== conditionId),
      });
    }
  };

  const updateCondition = (conditionId, updates, groupIndex = null) => {
    const newConfig = { ...filterConfig };

    if (groupIndex !== null) {
      const conditionIndex = newConfig.conditions[
        groupIndex
      ].conditions.findIndex((c) => c.id === conditionId);

      if (conditionIndex !== -1) {
        newConfig.conditions[groupIndex].conditions[conditionIndex] = {
          ...newConfig.conditions[groupIndex].conditions[conditionIndex],
          ...updates,
        };

        if (updates.field) {
          const fieldInfo = LEAD_FIELDS.find((f) => f.value === updates.field);
          if (fieldInfo) {
            newConfig.conditions[groupIndex].conditions[
              conditionIndex
            ].dataType = fieldInfo.type;
            newConfig.conditions[groupIndex].conditions[
              conditionIndex
            ].operator = "";
            newConfig.conditions[groupIndex].conditions[conditionIndex].value =
              "";
          }
        }
      }
    } else {
      const conditionIndex = newConfig.conditions.findIndex(
        (c) => c.id === conditionId,
      );

      if (conditionIndex !== -1) {
        newConfig.conditions[conditionIndex] = {
          ...newConfig.conditions[conditionIndex],
          ...updates,
        };

        if (updates.field) {
          const fieldInfo = LEAD_FIELDS.find((f) => f.value === updates.field);
          if (fieldInfo) {
            newConfig.conditions[conditionIndex].dataType = fieldInfo.type;
            newConfig.conditions[conditionIndex].operator = "";
            newConfig.conditions[conditionIndex].value = "";
          }
        }
      }
    }

    setFilterConfig(newConfig);
    onApply(newConfig);
  };

  const updateGroupLogic = (groupIndex, logic) => {
    const newConfig = { ...filterConfig };
    newConfig.conditions[groupIndex].logic = logic;
    setFilterConfig(newConfig);
    onApply(newConfig);
  };

  const updateRootLogic = (logic) => {
    const newConfig = { ...filterConfig, logic };
    setFilterConfig(newConfig);
    onApply(newConfig);
  };

  const handleApply = () => {
    const hasValidConditions = filterConfig.conditions.some((condition) => {
      if (condition.type === "group") {
        return condition.conditions.some((c) => c.field && c.operator);
      }
      return condition.field && condition.operator;
    });

    if (!hasValidConditions) {
      alert("Please add at least one valid filter condition");
      return;
    }

    onApply(filterConfig);
  };

  const handleSave = () => {
    const hasValidConditions = filterConfig.conditions.some((condition) => {
      if (condition.type === "group") {
        return condition.conditions.some((c) => c.field && c.operator);
      }
      return condition.field && condition.operator;
    });

    if (!hasValidConditions) {
      alert("Please add at least one valid filter condition before saving");
      return;
    }
    onSave();
  };

  const renderCondition = (condition, index, groupIndex = null) => {
    const fieldInfo = LEAD_FIELDS.find((f) => f.value === condition.field);
    const operators = fieldInfo ? OPERATORS[fieldInfo.type] || [] : [];
    const needsValue = !OPERATORS_WITHOUT_VALUE.includes(condition.operator);

    return (
      <div
        key={condition.id}
        className={`flex flex-wrap items-start gap-2 p-3 rounded-lg border ${
          isDarkMode
            ? "bg-gray-800/50 border-gray-700"
            : "bg-gray-50 border-gray-200"
        }`}
      >
        {/* Field Selector */}
        <select
          value={condition.field}
          onChange={(e) =>
            updateCondition(condition.id, { field: e.target.value }, groupIndex)
          }
          className={`flex-1 min-w-[200px] ${selectCls}`}
        >
          <option value="">Select Field...</option>
          {LEAD_FIELDS.map((field) => (
            <option key={field.value} value={field.value}>
              {field.label}
            </option>
          ))}
        </select>

        {/* Operator Selector */}
        {condition.field && (
          <select
            value={condition.operator}
            onChange={(e) =>
              updateCondition(
                condition.id,
                { operator: e.target.value },
                groupIndex,
              )
            }
            className={`flex-1 min-w-[180px] ${selectCls}`}
          >
            <option value="">Select Operator...</option>
            {operators.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
        )}

        {/* Value Input */}
        {condition.field && condition.operator && needsValue && (
          <>
            {fieldInfo?.type === "select" && fieldInfo.options ? (
              condition.operator === "in" || condition.operator === "not_in" ? (
                <select
                  multiple
                  value={Array.isArray(condition.value) ? condition.value : []}
                  onChange={(e) => {
                    const values = Array.from(
                      e.target.selectedOptions,
                      (option) => option.value,
                    );
                    updateCondition(
                      condition.id,
                      { value: values },
                      groupIndex,
                    );
                  }}
                  className={`flex-1 min-w-[200px] ${selectCls}`}
                  size={Math.min(fieldInfo.options.length, 5)}
                >
                  {fieldInfo.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  value={condition.value}
                  onChange={(e) =>
                    updateCondition(
                      condition.id,
                      { value: e.target.value },
                      groupIndex,
                    )
                  }
                  className={`flex-1 min-w-[200px] ${selectCls}`}
                >
                  <option value="">Select Value...</option>
                  {fieldInfo.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )
            ) : condition.operator === "between" ? (
              <div className="flex-1 min-w-[200px] flex gap-2">
                <input
                  type={
                    fieldInfo?.type === "date"
                      ? "date"
                      : fieldInfo?.type === "number" ||
                          fieldInfo?.type === "decimal"
                        ? "number"
                        : "text"
                  }
                  value={
                    Array.isArray(condition.value)
                      ? condition.value[0] || ""
                      : ""
                  }
                  onChange={(e) => {
                    const currentValue = Array.isArray(condition.value)
                      ? condition.value
                      : ["", ""];
                    updateCondition(
                      condition.id,
                      { value: [e.target.value, currentValue[1]] },
                      groupIndex,
                    );
                  }}
                  placeholder="From"
                  className={`flex-1 ${inputCls}`}
                />
                <input
                  type={
                    fieldInfo?.type === "date"
                      ? "date"
                      : fieldInfo?.type === "number" ||
                          fieldInfo?.type === "decimal"
                        ? "number"
                        : "text"
                  }
                  value={
                    Array.isArray(condition.value)
                      ? condition.value[1] || ""
                      : ""
                  }
                  onChange={(e) => {
                    const currentValue = Array.isArray(condition.value)
                      ? condition.value
                      : ["", ""];
                    updateCondition(
                      condition.id,
                      { value: [currentValue[0], e.target.value] },
                      groupIndex,
                    );
                  }}
                  placeholder="To"
                  className={`flex-1 ${inputCls}`}
                />
              </div>
            ) : condition.operator === "last_n_days" ||
              condition.operator === "next_n_days" ? (
              <input
                type="number"
                value={condition.value}
                onChange={(e) =>
                  updateCondition(
                    condition.id,
                    { value: e.target.value },
                    groupIndex,
                  )
                }
                placeholder="Number of days"
                className={`flex-1 min-w-[200px] ${inputCls}`}
              />
            ) : condition.operator === "contains_all" ||
              condition.operator === "in" ||
              condition.operator === "not_in" ? (
              <div className="flex-1 min-w-[200px]">
                <input
                  type="text"
                  value={condition.value}
                  onChange={(e) =>
                    updateCondition(
                      condition.id,
                      { value: e.target.value },
                      groupIndex,
                    )
                  }
                  placeholder="Enter values separated by commas"
                  className={`w-full ${inputCls}`}
                />
                <p
                  className={`text-xs mt-1 ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Separate multiple values with commas
                </p>
              </div>
            ) : (
              <input
                type={
                  fieldInfo?.type === "date"
                    ? "date"
                    : fieldInfo?.type === "number" ||
                        fieldInfo?.type === "decimal"
                      ? "number"
                      : "text"
                }
                value={condition.value}
                onChange={(e) =>
                  updateCondition(
                    condition.id,
                    { value: e.target.value },
                    groupIndex,
                  )
                }
                placeholder="Enter value..."
                step={fieldInfo?.type === "decimal" ? "0.01" : undefined}
                className={`flex-1 min-w-[200px] ${inputCls}`}
              />
            )}
          </>
        )}

        {/* Remove Button */}
        <button
          onClick={() => removeCondition(condition.id, groupIndex)}
          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-colors"
          title="Remove condition"
        >
          <Trash2 size={16} />
        </button>
      </div>
    );
  };

  const renderConditionGroup = (group, groupIndex) => {
    return (
      <div
        key={group.id}
        className={`p-4 rounded-lg border-2 border-dashed ${
          isDarkMode
            ? "bg-gray-800/30 border-gray-600"
            : "bg-gray-50 border-gray-300"
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Match
            </span>
            <select
              value={group.logic}
              onChange={(e) => updateGroupLogic(groupIndex, e.target.value)}
              className={`px-3 py-1 rounded text-sm focus:outline-none focus:border-blue-500 ${
                isDarkMode
                  ? "bg-gray-700 border border-gray-600 text-white"
                  : "bg-white border border-gray-300 text-gray-900"
              }`}
            >
              <option value="AND">All</option>
              <option value="OR">Any</option>
            </select>
            <span
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              of these conditions:
            </span>
          </div>

          <button
            onClick={() => {
              const newConfig = {
                ...filterConfig,
                conditions: filterConfig.conditions.filter(
                  (_, i) => i !== groupIndex,
                ),
              };
              setFilterConfig(newConfig);
              onApply(newConfig);
            }}
            className="text-red-400 hover:text-red-300 text-sm"
          >
            Remove Group
          </button>
        </div>

        <div className="space-y-2">
          {group.conditions.map((condition, index) =>
            renderCondition(condition, index, groupIndex),
          )}
        </div>

        <button
          onClick={() => addCondition(groupIndex)}
          className="mt-3 flex items-center gap-2 px-3 py-2 text-blue-400 hover:text-blue-300 text-sm"
        >
          <Plus size={16} />
          Add Condition
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {filterConfig.conditions.length > 0 && (
        <div
          className={`flex items-center gap-3 p-3 rounded-lg border ${
            isDarkMode
              ? "bg-gray-800/50 border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <span
            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            Match
          </span>
          <select
            value={filterConfig.logic}
            onChange={(e) => updateRootLogic(e.target.value)}
            className={`px-3 py-1.5 rounded-lg text-sm focus:outline-none focus:border-blue-500 ${
              isDarkMode
                ? "bg-gray-700 border border-gray-600 text-white"
                : "bg-white border border-gray-300 text-gray-900"
            }`}
          >
            <option value="AND">All</option>
            <option value="OR">Any</option>
          </select>
          <span
            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            of the following:
          </span>
        </div>
      )}

      <div className="space-y-3">
        {filterConfig.conditions.map((item, index) => {
          if (item.type === "group") {
            return renderConditionGroup(item, index);
          }
          return renderCondition(item, index);
        })}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => addCondition()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
        >
          <Plus size={16} />
          Add Condition
        </button>

        <button
          onClick={addConditionGroup}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm ${
            isDarkMode
              ? "bg-gray-700 hover:bg-gray-600 text-white"
              : "bg-gray-200 hover:bg-gray-300 text-gray-700"
          }`}
        >
          <Plus size={16} />
          Add Condition Group
        </button>
      </div>

      <div
        className={`flex justify-end gap-2 pt-4 border-t ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        {showSaveButton && (
          <button
            onClick={handleSave}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              isDarkMode
                ? "bg-gray-700 hover:bg-gray-600 text-white"
                : "bg-gray-200 hover:bg-gray-300 text-gray-700"
            }`}
          >
            <Save size={16} />
            Save Filter
          </button>
        )}

        <button
          onClick={handleApply}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <Filter size={16} />
          Apply Filters
        </button>
      </div>

      {filterConfig.conditions.length > 0 && (
        <div
          className={`p-3 rounded-lg border ${
            isDarkMode
              ? "bg-gray-800/30 border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <div
            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Active Filters: {filterConfig.conditions.length} condition
            {filterConfig.conditions.length !== 1 ? "s" : ""}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedFilterBuilder;
